// src/pages/product/Products.tsx
//
// Products page
// - Uses shared SearchPanel via ProductsSearchPanel.
// - Keeps URL query params in sync with the applied search state (shareable deep links).
// - Uses "draft vs applied" state to avoid filtering while typing.
// - Auto-search is opt-in per page and handled by SearchPanel (do NOT implement debounce here).
//
// Policy alignment:
// - SearchPanel owns debounce/timers when autoSearch is enabled.
// - The page owns draft/applied states and defines what "apply" means (draft -> applied + URL + page reset).
// - Do not manually refetch in effects; queries should react to input param changes.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { ProductStatus } from '@models/product';
import { exportProductsExcel, type GetProductsParams, ProductSaleType } from '@apis/product';

import { useProductList } from '@queries/products/useProductList';
import { useProductCount } from '@queries/products/useProductCount';
import { useCountryList } from '@queries/countries/useCountryList';
import { useAllStockList } from '@queries/stocks/useAllStockList';

import useAsyncEffect from '@hooks/useAsyncEffect';
import { filterSearchParams } from '@utils/search';
import { KOREAN_TIME_ZONE } from '@constants/timezone';
import { utcToZonedTime } from 'date-fns-tz';
import { downloadBlob } from '@utils/file';

import ProductsTable from '@components/product/ProductsTable';
import ProductsSearchPanel from '@components/product/ProductsSearchPanel';
import { usePageHeader } from '@components/common/pageHeader.context';

type ProductSearchForm = GetProductsParams;

type Option<T> = {
  label: string;
  value: T;
};

const PAGE_SIZE = 50;

/**
 * Page-level auto-search policy:
 * - Global default autoSearch is OFF (SearchPanel constants).
 * - This page can opt in by setting enabled=true.
 */
const AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

const parsePositiveInt = (value: string | null, fallback = 1): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const parseNumberParam = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const isValidEnumValue = <T extends string>(values: readonly string[], v: string | null): v is T => {
  if (!v) return false;
  return values.includes(v);
};

/**
 * Normalize a search form:
 * - Trim string fields
 * - Drop empty values (undefined / null / empty trimmed string)
 */
const cleanSearchForm = (form: ProductSearchForm): ProductSearchForm => {
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(form)) {
    if (value === undefined || value === null) continue;

    if (typeof value === 'string') {
      const v = value.trim();
      if (!v) continue;
      cleaned[key] = v;
      continue;
    }

    cleaned[key] = value;
  }

  return cleaned as ProductSearchForm;
};

const readSearchFromUrl = (sp: URLSearchParams): ProductSearchForm => {
  const saleTypeRaw = sp.get('productSaleType');
  const statusRaw = sp.get('productStatus');

  const productSaleType = isValidEnumValue<ProductSaleType>(
    Object.values(ProductSaleType) as unknown as string[],
    saleTypeRaw,
  )
    ? (saleTypeRaw as ProductSaleType)
    : undefined;

  const productStatus = isValidEnumValue<ProductStatus>(
    Object.values(ProductStatus) as unknown as string[],
    statusRaw,
  )
    ? (statusRaw as ProductStatus)
    : undefined;

  return {
    productCode: sp.get('productCode') || '',
    productSaleType,
    productName: sp.get('productName') || '',
    countryId: parseNumberParam(sp.get('countryId')),
    stockId: parseNumberParam(sp.get('stockId')),
    productStatus,
  };
};

const buildUrlParams = (form: ProductSearchForm, page: number) => {
  const cleaned = cleanSearchForm(form);
  const params: Record<string, string> = {};

  for (const [k, v] of Object.entries(cleaned)) {
    params[k] = String(v);
  }

  return {
    ...filterSearchParams(Object.fromEntries(new URLSearchParams(params).entries())),
    page: String(page),
  };
};

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setPageHeaderSlots } = usePageHeader();

  /* ------------------------------------------------------------------
   * State: draft vs applied + URL sync
   * ------------------------------------------------------------------ */

  const [draftSearch, setDraftSearch] = useState<ProductSearchForm>(() => readSearchFromUrl(searchParams));
  const [appliedSearch, setAppliedSearch] = useState<ProductSearchForm>(() => cleanSearchForm(readSearchFromUrl(searchParams)));
  const [page, setPage] = useState<number>(() => parsePositiveInt(searchParams.get('page'), 1));

  /**
   * Keep state in sync when URL changes
   * (deep link / back-forward / manual URL edits).
   */
  useEffect(() => {
    const nextDraft = readSearchFromUrl(searchParams);
    const nextApplied = cleanSearchForm(nextDraft);
    const nextPage = parsePositiveInt(searchParams.get('page'), 1);

    setDraftSearch(nextDraft);
    setAppliedSearch(nextApplied);
    setPage(nextPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const cleanedApplied = useMemo(() => cleanSearchForm(appliedSearch), [appliedSearch]);

  const syncUrlFromApplied = useCallback(
    (nextApplied: ProductSearchForm, nextPage: number) => {
      setSearchParams(buildUrlParams(nextApplied, nextPage));
    },
    [setSearchParams],
  );

  /* ------------------------------------------------------------------
   * Actions: apply / reset
   * ------------------------------------------------------------------ */

  const applyDraftAsApplied = useCallback(() => {
    const nextApplied = cleanSearchForm(draftSearch);
    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [draftSearch, syncUrlFromApplied]);

  const resetAll = useCallback(() => {
    const cleared: ProductSearchForm = {
      productCode: '',
      productSaleType: undefined,
      productName: '',
      countryId: undefined,
      stockId: undefined,
      productStatus: undefined,
    };

    setDraftSearch(cleared);

    const nextApplied = cleanSearchForm(cleared);
    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [syncUrlFromApplied]);

  /* ------------------------------------------------------------------
   * Paging (URL sync)
   * ------------------------------------------------------------------ */

  const handleChangePage = useCallback(
    (nextPage: number) => {
      const safeNext = Math.max(1, nextPage);
      setPage(safeNext);
      syncUrlFromApplied(appliedSearch, safeNext);
    },
    [appliedSearch, syncUrlFromApplied],
  );

  /* ------------------------------------------------------------------
   * Draft setter (shared contract: null means clear)
   * ------------------------------------------------------------------ */

  const handleChangeInput = useCallback(
    <K extends keyof ProductSearchForm>(name: K, value: ProductSearchForm[K] | null) => {
      setDraftSearch((prev) => ({
        ...prev,
        [name]: (value ?? undefined) as ProductSearchForm[K],
      }));
    },
    [],
  );

  /* ------------------------------------------------------------------
   * Queries (driven by applied + page)
   * ------------------------------------------------------------------ */

  // NOTE: useProductList signature is { page, searchParam } (no pageSize).
  const { data, isLoading } = useProductList({ page, searchParam: cleanedApplied });
  const { data: totalCount, isLoading: isLoadingCount } = useProductCount(cleanedApplied);

  /* ------------------------------------------------------------------
   * Export (based on applied)
   * ------------------------------------------------------------------ */

  const [isExporting, setExporting] = useState(false);

  const buildExportFileName = (prefix: string) => {
    const kstDate = utcToZonedTime(new Date(), KOREAN_TIME_ZONE);
    return `${prefix}-${kstDate.toISOString()}.xlsx`;
  };

  const handleOnExport = useCallback(async () => {
    if (isExporting) return;

    try {
      setExporting(true);
      const res = await exportProductsExcel(cleanedApplied);
      downloadBlob(res, buildExportFileName('products'));
    } finally {
      setExporting(false);
    }
  }, [cleanedApplied, isExporting]);

  /* ------------------------------------------------------------------
   * Dropdown options
   * ------------------------------------------------------------------ */

  const { data: countriesData } = useCountryList();
  const { data: stocksData } = useAllStockList();

  const [countryOptionList, setCountryOptionList] = useState<Array<Option<number>>>([]);
  const [stockOptionList, setStockOptionList] = useState<Array<Option<number>>>([]);

  useAsyncEffect(async () => {
    if (!countriesData) return;

    const options: Array<Option<number>> = (countriesData.data ?? [])
      .map((c) => ({ label: c.englishName, value: c.countryId }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setCountryOptionList(options);
  }, [countriesData]);

  useAsyncEffect(async () => {
    if (!stocksData) return;

    const options: Array<Option<number>> = (stocksData.data ?? [])
      .map((s) => ({ label: s.stockCode, value: s.stockId }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setStockOptionList(options);
  }, [stocksData]);

  /* ------------------------------------------------------------------
   * Header action: Add Product
   * ------------------------------------------------------------------ */

  const handleOnAdd = useCallback(() => {
    navigate('/products/add');
  }, [navigate]);

  useEffect(() => {
    setPageHeaderSlots({
      actions: [
        {
          key: 'add-product',
          label: 'Add Product',
          startIcon: <AddIcon />,
          onClick: handleOnAdd,
          variant: 'contained',
          primary: true,
        },
      ],
    });

    // Clear on unmount to avoid leaking header actions across routes.
    return () => setPageHeaderSlots({});
  }, [handleOnAdd, setPageHeaderSlots]);

  /* ------------------------------------------------------------------
   * Render
   * ------------------------------------------------------------------ */

  const isTableLoading = Boolean(isLoading || isLoadingCount);

  return (
    <Box>
      <ProductsSearchPanel
        searchForm={draftSearch}
        onChangeInput={handleChangeInput}
        onSearch={applyDraftAsApplied}
        onReset={resetAll}
        onExport={handleOnExport}
        isExporting={isExporting}
        autoSearch={AUTO_SEARCH}
        countryOptions={countryOptionList}
        stockOptions={stockOptionList}
      />

      {/* Standard controls are rendered only inside ProductsTable (single source of list controls). */}
      <Box mt={2}>
        <ProductsTable
          data={data?.data || []}
          isLoading={isTableLoading}
          totalCount={totalCount ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          onChangePage={handleChangePage}
        />
      </Box>
    </Box>
  );
};

export default Products;
